<template>
  <div class="page">
    <div class="page-header">
      <h2>团队管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="showCreateTeamDialog">
        创建团队
      </el-button>
    </div>

    <div class="page-content">
      <div class="tree-container">
        <el-tree
          :data="teamData"
          :props="{ label: 'name', children: 'children' }"
          node-key="id"
          ref="tree"
          :expanded-keys="expandedKeys"
          @node-click="handleNodeClick"
          @node-expand="handleNodeExpand"
          @node-collapse="handleNodeCollapse"
        >
          <span class="tree-node" slot-scope="{ node, data }">
            <div class="node-content">
              <div class="node-info">
                <span
                  class="node-icon"
                  :class="getNodeIconClass(data.nodeType)"
                  :style="getNodeStyle(data)"
                >
                  <i :class="getNodeIcon(data.nodeType)"></i>
                </span>

                <div class="node-content-wrapper">
                  <div class="node-main">
                    <span class="node-label">
                      {{ node.label }}
                    </span>

                    <!-- 团队显示：队长 + 类型 + 小组个数 -->
                    <template v-if="data.nodeType === 'team'">
                      <span class="role-info team-leader-info" v-if="data.leader">
                        队长：{{ data.leader.screen_name }}
                      </span>
                      <span class="node-type-tag" :class="`type-${data.kind || 'normal'}`">
                        {{ getKindDisplayName(data.kind) }}
                      </span>
                      <span class="children-count-tag">
                        {{ data.children_count || 0 }}个小组
                      </span>
                    </template>

                    <!-- 小组显示：组长 + 跟单员 + 人数 -->
                    <template v-else-if="data.nodeType === 'group'">
                      <span class="role-info leader-info" v-if="data.leader">
                        组长：{{ data.leader.screen_name }}
                      </span>
                      <span class="role-info merchandiser-info" v-if="data.merchandiser">
                        跟单员：{{ data.merchandiser.screen_name }}
                      </span>
                      <span class="children-count-tag">
                        {{ (data.children && data.children.length) || 0 }}人
                      </span>
                    </template>

                    <!-- 操作按钮直接跟在内容后面 -->
                    <div class="node-actions">
                    <template v-if="data.nodeType === 'team'">
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-plus"
                        @click.stop="showCreateGroupDialog(data)"
                      >
                        创建小组
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-edit"
                        @click.stop="editTeam(data)"
                      >
                        编辑
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-delete"
                        @click.stop="deleteNode(data)"
                      >
                        删除
                      </el-button>
                    </template>

                    <template v-else-if="data.nodeType === 'group'">
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-edit"
                        @click.stop="editGroup(data)"
                      >
                        编辑
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-user-solid"
                        @click.stop="showAddMemberDialog(data)"
                      >
                        添加成员
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-delete"
                        @click.stop="deleteNode(data)"
                      >
                        删除
                      </el-button>
                    </template>

                    <template v-else-if="data.nodeType === 'member'">
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-user"
                        @click.stop="setAsLeader(data)"
                      >
                        设为组长
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-s-custom"
                        @click.stop="setAsMerchandiser(data)"
                      >
                        设为跟单员
                      </el-button>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-delete"
                        @click.stop="removeMemberFromGroup(data)"
                      >
                        移除
                      </el-button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </span>
        </el-tree>
      </div>
    </div>

    <!-- 团队/小组表单弹窗 -->
    <team-form-dialog
      v-model="teamDialogVisible"
      :title="teamDialogTitle"
      :data="currentEditData"
      :type="currentEditType"
      :parent-id="currentParentData ? currentParentData.id : null"
      :parent-team="currentParentData"
      :users="users"
      @team-created="handleTeamCreated"
      @team-updated="handleTeamUpdated"
      @group-created="handleGroupCreated"
      @group-updated="handleGroupUpdated"
    />

    <!-- 添加成员弹窗 -->
    <add-member-dialog
      v-model="addMemberDialogVisible"
      :group-data="currentGroupData"
      :users="users"
      :exclude-user-ids="excludeUserIds"
      @members-added="handleMembersAdded"
    />
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import TeamFormDialog from './components/team-form-dialog.vue';
import AddMemberDialog from './components/add-member-dialog.vue';
import { request } from 'beans-admin-plugin';

@Component({
  components: {
    TeamFormDialog,
    AddMemberDialog
  }
})
export default class Page extends Vue {
  teamData = [];
  expandedKeys = []; // 用于跟踪展开的节点
  users = []; // 所有用户数据

  teamDialogVisible = false
  teamDialogTitle = ''
  currentEditData = {}
  currentEditType = 'team'
  currentParentData = null

  addMemberDialogVisible = false
  currentGroupData = null
  excludeUserIds = []

  async getTeams() {
    // if (this.teamData.length) {
    //   return this.teamData;
    // }
    const { data } = await request.get('/teams', { params: { parent_id_null: true }  });
    this.teamData = data.map(item => ({
      ...item,
      nodeType: 'team',
      children: [],
      children_count: item.children_count || 0,
    }));
    return this.teamData;
  }

  async fetchUsers() {
    try {
      const users = await this.$request.get('/users/read_options', {
        page: 0
      });
      this.users = users;
    } catch (error) {
      console.error('获取用户数据失败:', error);
      this.users = [];
    }
  }

  async getGroups(id) {
    const { data } = await this.$autoLoading(
      this.$request.get('/teams', { params: { parent_id_eq: id } })
    );
    const parentIndex = this.teamData.findIndex(item => item.id === id);
    if (parentIndex !== -1) {
      // 处理小组数据，添加必要属性
      const groups = data.map(group => {
        return {
          ...group,
          nodeType: 'group',
          children: (group.team_members && Array.isArray(group.team_members)) ? group.team_members.map(member => {
            return {
              id: `member_${member.id}`,
              name: member.screen_name || member.name || `用户${member.id}`,
              nodeType: 'member',
              memberData: member
            };
          }) : []
        };
      });
      // 使用Vue.set确保响应式更新
      this.$set(this.teamData[parentIndex], 'children', groups);
      this.maintainExpandedState();
    }
  }

  mounted() {
    this.getTeams();
    this.fetchUsers();
  }


  showCreateTeamDialog() {
    this.teamDialogTitle = '创建团队'
    this.currentEditType = 'team'
    this.currentEditData = {}
    this.currentParentData = null
    this.teamDialogVisible = true
  }

  showCreateGroupDialog(teamData) {
    this.teamDialogTitle = '创建小组'
    this.currentEditType = 'group'
    this.currentEditData = {
      kind: teamData.kind,
      color: teamData.color
    }
    this.currentParentData = teamData
    this.teamDialogVisible = true
  }

  editTeam(data) {
    this.teamDialogTitle = '编辑团队'
    this.currentEditType = 'team'
    this.currentEditData = { ...data }
    this.currentParentData = null
    this.teamDialogVisible = true
  }

  editGroup(data) {
    this.teamDialogTitle = '编辑小组'
    this.currentEditType = 'group'
    this.currentEditData = { ...data }
    this.currentParentData = null
    this.teamDialogVisible = true
  }

  showAddMemberDialog(groupData) {
    this.currentGroupData = { ...groupData }

    // 收集需要过滤的用户ID：组长、队长
    const excludeIds = []

    // 添加组长ID
    if (groupData.leader && groupData.leader.id) {
      excludeIds.push(groupData.leader.id)
    } else if (groupData.leader_id) {
      excludeIds.push(groupData.leader_id)
    }

    // 添加跟单员ID
    if (groupData.merchandiser && groupData.merchandiser.id) {
      excludeIds.push(groupData.merchandiser.id)
    } else if (groupData.merchandiser_id) {
      excludeIds.push(groupData.merchandiser_id)
    }

    // 查找该小组所属的团队，添加队长ID
    const parentTeam = this.teamData.find(team =>
      team.children && team.children.some(group => group.id === groupData.id)
    )
    if (parentTeam && parentTeam.leader && parentTeam.leader.id) {
      excludeIds.push(parentTeam.leader.id)
    } else if (parentTeam && parentTeam.leader_id) {
      excludeIds.push(parentTeam.leader_id)
    }

    // 去重并保存
    this.excludeUserIds = [...new Set(excludeIds.filter(id => id))]

    this.addMemberDialogVisible = true
  }

  // 处理团队创建事件
  async handleTeamCreated({ data }) {
    // 将新团队添加到本地数据
    const newTeam = {
      ...data,
      nodeType: 'team',
      children: [],
      children_count: data.children_count || 0,
    }
    this.teamData.push(newTeam)
    this.maintainExpandedState()
  }

  // 处理团队更新事件
  async handleTeamUpdated({ data, originalData }) {
    // 更新本地团队数据
    const teamIndex = this.teamData.findIndex(team => team.id === originalData.id)
    if (teamIndex !== -1) {
      const currentChildren = this.teamData[teamIndex].children || []
      const updatedTeam = {
        ...data,
        nodeType: 'team',
        children: currentChildren,
        children_count: data.children_count || currentChildren.length,
      }
      // 使用Vue.set确保响应式更新
      this.$set(this.teamData, teamIndex, updatedTeam)
      this.maintainExpandedState()
    }
  }

  // 处理小组创建事件
  async handleGroupCreated({ data, parentId }) {
    // 将新小组添加到对应团队的children中
    const parentTeamIndex = this.teamData.findIndex(team => team.id === parentId)
    if (parentTeamIndex !== -1) {
      if (!this.teamData[parentTeamIndex].children) {
        this.$set(this.teamData[parentTeamIndex], 'children', [])
      }

      const newGroup = {
        ...data,
        nodeType: 'group',
        children: data.team_members ? data.team_members.map(member => ({
          id: `member_${member.id}`,
          name: member.screen_name,
          nodeType: 'member',
          memberData: member
        })) : []
      }
      this.teamData[parentTeamIndex].children.push(newGroup)

      // 更新父团队的children_count字段
      this.teamData[parentTeamIndex].children_count = this.teamData[parentTeamIndex].children.length

      // 展开父节点显示新创建的小组
      this.$nextTick(() => {
        const parentNode = this.$refs.tree.store.getNode(parentId)
        if (parentNode) {
          parentNode.expanded = true
          if (!this.expandedKeys.includes(parentId)) {
            this.expandedKeys.push(parentId)
          }
        }
      })
      this.maintainExpandedState()
    }
  }

  // 处理小组更新事件
  async handleGroupUpdated({ data, originalData }) {
    try {
      // 创建更新后的小组数据结构
      const updatedGroup = {
        ...data,
        nodeType: 'group',
        children: (data.team_members && Array.isArray(data.team_members)) ? data.team_members.map(member => ({
          id: `member_${member.id}`,
          name: member.screen_name || member.name || `用户${member.id}`,
          nodeType: 'member',
          memberData: member
        })) : []
      }
      // 更新本地数据
      this.updateGroupInTeamData(originalData.id, updatedGroup)
      this.maintainExpandedState()
    } catch (error) {
      this.$message.error('小组数据更新失败')
    }
  }




  // 更新团队数据中的小组信息
  updateGroupInTeamData(groupId, updatedGroupData) {
    for (let i = 0; i < this.teamData.length; i++) {
      const team = this.teamData[i];
      if (team.children && team.children.length > 0) {
        const groupIndex = team.children.findIndex(group => group.id === groupId);
        if (groupIndex !== -1) {
          // 直接使用传入的已处理好的数据
          this.$set(team.children, groupIndex, updatedGroupData);
          break;
        }
      }
    }
  }

  // 通用方法：查找成员所属的小组和团队
  findMemberGroup(memberData) {
    for (const team of this.teamData) {
      if (team.children && team.children.length > 0) {
        for (const group of team.children) {
          if (group.children && group.children.some(member => member.id === memberData.id)) {
            return { group, team }
          }
        }
      }
    }
    return { group: null, team: null }
  }

  // 获取真实成员ID（处理member_前缀）
  getRealMemberId(memberData) {
    return (memberData.memberData && memberData.memberData.id) ||
      (memberData.id.toString().startsWith('member_') ?
        parseInt(memberData.id.replace('member_', '')) : memberData.id)
  }

  // 通用方法：更新小组数据
  async updateGroupData(groupId, payload) {
    const data = await this.$autoLoading(this.$request.put(`/teams/${groupId}`, payload))

    // 创建标准化的小组数据结构
    const standardizedGroup = {
      ...data,
      nodeType: 'group',
      children: (data.team_members && Array.isArray(data.team_members)) ? data.team_members.map(member => ({
        id: `member_${member.id}`,
        name: member.screen_name || member.name || `用户${member.id}`,
        nodeType: 'member',
        memberData: member
      })) : []
    }

    this.updateGroupInTeamData(groupId, standardizedGroup)
    this.maintainExpandedState()
    return data
  }

  // 通用方法：设置小组角色
  async setGroupRole(memberData, roleType) {
    const { group: targetGroup } = this.findMemberGroup(memberData);
    if (!targetGroup) {
      this.$message.error('未找到该成员所属的小组');
      return;
    }

    const newRoleId = this.getRealMemberId(memberData);
    const roleKey = roleType === 'leader' ? 'leader_id' : 'merchandiser_id';
    const currentRoleId =
      (targetGroup[roleType] && targetGroup[roleType].id) ||
      targetGroup[roleKey];
    const roleName = roleType === 'leader' ? '组长' : '跟单员';

    if (currentRoleId === newRoleId) {
      this.$message.info(`${memberData.name} 已经是${roleName}了`);
      return;
    }

    const oldRoleName =
      (targetGroup[roleType] && targetGroup[roleType].screen_name) || '无';
    await this.$confirm(
      `确定要将'${memberData.name}'设置为小组'${targetGroup.name}'的${roleName}吗？\n当前${roleName}：${oldRoleName}`,
      `设置${roleName}`,
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'info' }
    );

    let updatedMemberIds =
      (targetGroup.team_members &&
        targetGroup.team_members.map((member) => member.id)) ||
      [];
    // 从成员列表中移除新角色，添加旧角色
    updatedMemberIds = updatedMemberIds.filter((id) => id !== newRoleId);
    if (
      currentRoleId &&
      currentRoleId !== newRoleId &&
      !updatedMemberIds.includes(currentRoleId)
    ) {
      updatedMemberIds.push(currentRoleId);
    }

    const payload = {
      name: targetGroup.name,
      kind: targetGroup.kind,
      color: targetGroup.color,
      leader_id:
        roleType === 'leader'
          ? newRoleId
          : (targetGroup.leader && targetGroup.leader.id) ||
            targetGroup.leader_id,
      merchandiser_id:
        roleType === 'merchandiser'
          ? newRoleId
          : (targetGroup.merchandiser && targetGroup.merchandiser.id) ||
            targetGroup.merchandiser_id,
      parent_id: targetGroup.parent_id,
      team_member_ids: updatedMemberIds,
    };

    await this.updateGroupData(targetGroup.id, payload);
    this.$message.success(`${memberData.name} 已设置为${roleName}`);
  }

  async deleteNode(data) {
    try {
      // 根据不同类型显示不同的确认信息
      let confirmMessage = `确定要删除${this.getNodeTypeName(data.nodeType)}"${data.name}"吗？`;
      if (data.nodeType === 'team') {
        confirmMessage += '\n删除团队将同时删除该团队下的所有小组！';
      } else if (data.nodeType === 'group') {
        confirmMessage += '\n删除小组将移除该小组下的所有成员！';
      }

      await this.$confirm(
        confirmMessage,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      );

      // 调用删除API
      await this.$autoLoading(
        request.delete(`/teams/${data.id}`)
      );

      // 根据不同类型执行不同的删除逻辑
      if (data.nodeType === 'team') {
        this.deleteTeamFromData(data);
      } else if (data.nodeType === 'group') {
        this.deleteGroupFromData(data);
      }

      this.$message.success(`${this.getNodeTypeName(data.nodeType)}删除成功`);
    } catch (error) {
      if (error !== 'cancel') {
        this.$message.error(`删除${this.getNodeTypeName(data.nodeType)}失败`);
      }
    }
  }

  // 从数据中删除团队
  deleteTeamFromData(teamData) {
    const index = this.teamData.findIndex(item => item.id === teamData.id);
    if (index !== -1) {
      this.teamData.splice(index, 1);
    }
  }

  // 从数据中删除小组
  deleteGroupFromData(groupData) {
    // 遍历所有团队，找到包含该小组的团队
    for (let i = 0; i < this.teamData.length; i++) {
      const team = this.teamData[i];
      if (team.children && team.children.length > 0) {
        const groupIndex = team.children.findIndex(group => group.id === groupData.id);
        if (groupIndex !== -1) {
          // 从父团队的children中移除该小组
          team.children.splice(groupIndex, 1);
          // 更新父团队的children_count字段
          team.children_count = team.children.length;
          break;
        }
      }
    }
  }

  // 从小组中移除成员
  async removeMemberFromGroup(memberData) {
    try {
      const { group: targetGroup } = this.findMemberGroup(memberData)
      if (!targetGroup) {
        this.$message.error('未找到该成员所属的小组')
        return
      }

      await this.$confirm(
        `确定要将"${memberData.name}"从小组"${targetGroup.name}"中移除吗？`,
        '移除确认',
        { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
      )

      const currentMemberIds = (targetGroup.team_members && targetGroup.team_members.map(member => member.id)) || []
      const memberIdToRemove = this.getRealMemberId(memberData)
      const newMemberIds = currentMemberIds.filter(id => id !== memberIdToRemove)

      const payload = {
        name: targetGroup.name,
        kind: targetGroup.kind,
        color: targetGroup.color,
        leader_id: (targetGroup.leader && targetGroup.leader.id) || targetGroup.leader_id,
        merchandiser_id: (targetGroup.merchandiser && targetGroup.merchandiser.id) || targetGroup.merchandiser_id,
        parent_id: targetGroup.parent_id,
        team_member_ids: newMemberIds
      }

      // 调用API更新小组
      const updatedGroupData = await this.$autoLoading(this.$request.put(`/teams/${targetGroup.id}`, payload))

      // 创建更新后的小组数据结构
      const updatedGroup = {
        ...updatedGroupData,
        nodeType: 'group',
        children: (updatedGroupData.team_members && Array.isArray(updatedGroupData.team_members)) ? updatedGroupData.team_members.map(member => ({
          id: `member_${member.id}`,
          name: member.screen_name || member.name || `用户${member.id}`,
          nodeType: 'member',
          memberData: member
        })) : []
      }

      // 更新本地数据
      this.updateGroupInTeamData(targetGroup.id, updatedGroup)
      this.maintainExpandedState()

      this.$message.success('成员移除成功')
    } catch (error) {
      if (error !== 'cancel') {
        this.$message.error('移除成员失败')
      }
    }
  }

  // 设置成员为组长
  setAsLeader(memberData) {
    return this.setGroupRole(memberData, 'leader')
  }

  // 设置成员为跟单员
  setAsMerchandiser(memberData) {
    return this.setGroupRole(memberData, 'merchandiser')
  }

  // 处理添加成员完成事件
  async handleMembersAdded({ groupId, updatedGroup }) {
    try {
      // 创建更新后的小组数据结构
      const updatedGroupData = {
        ...updatedGroup,
        nodeType: 'group',
        children: (updatedGroup.team_members && Array.isArray(updatedGroup.team_members)) ? updatedGroup.team_members.map(member => ({
          id: `member_${member.id}`,
          name: member.screen_name || member.name || `用户${member.id}`,
          nodeType: 'member',
          memberData: member
        })) : []
      }

      // 更新本地数据
      this.updateGroupInTeamData(groupId, updatedGroupData)
      this.maintainExpandedState()
    } catch (error) {
      this.$message.error('数据更新失败，请刷新页面重试')
    }
  }

  getNodeIconClass(nodeType) {
    return {
      'team-icon': nodeType === 'team',
      'group-icon': nodeType === 'group',
      'member-icon': nodeType === 'member'
    }
  }

  getNodeIcon(nodeType) {
    switch (nodeType) {
      case 'team':
        return 'el-icon-office-building'
      case 'group':
        return 'el-icon-s-cooperation'
      case 'member':
        return 'el-icon-user'
      default:
        return 'el-icon-document'
    }
  }

  getNodeStyle(data) {
    if (data.color && data.nodeType !== 'member') {
      return {
        color: data.color,
        borderColor: data.color
      }
    }
    return {}
  }

  getNodeTypeName(nodeType) {
    switch (nodeType) {
      case 'team':
        return '团队'
      case 'group':
        return '小组'
      case 'member':
        return '成员'
      default:
        return ''
    }
  }

  getKindDisplayName(kind) {
    switch (kind) {
      case 'normal':
        return '一般'
      case 'purple':
        return '紫队'
      case 'golden':
        return '金队'
      default:
        return '一般'
    }
  }

  async handleNodeClick(data) {
    if (data.nodeType === 'team' && (!data.children || data.children.length === 0)) {
      // 如果是团队节点且还没有加载子数据，则加载小组数据
      await this.getGroups(data.id);
      // 加载完数据后自动展开节点并保存状态
      this.$nextTick(() => {
        this.$refs.tree.store.getNode(data.id).expanded = true;
        if (!this.expandedKeys.includes(data.id)) {
          this.expandedKeys.push(data.id);
        }
      });
    }
  }

  // 处理节点展开事件
  handleNodeExpand(data) {
    if (!this.expandedKeys.includes(data.id)) {
      this.expandedKeys.push(data.id);
    }
  }

  // 处理节点折叠事件
  handleNodeCollapse(data) {
    const index = this.expandedKeys.indexOf(data.id);
    if (index > -1) {
      this.expandedKeys.splice(index, 1);
    }
  }

  // 保持展开状态的工具方法
  maintainExpandedState() {
    this.$nextTick(() => {
      if (this.$refs.tree && this.expandedKeys.length > 0) {
        // 确保展开状态在DOM更新后生效
        this.expandedKeys.forEach(key => {
          const node = this.$refs.tree.store.getNode(key);
          if (node && !node.expanded) {
            node.expanded = true;
          }
        });
      }
    });
  }
}

export const route = [
  {
    path: '/teams',
    name: 'teams.index',
    component: Page,
    meta: {
      can: 'teams.read_perfomance',
    }
  },
]
</script>

<style lang="scss" scoped>
.page {
  padding: 20px;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #303133;
    }
  }

  .page-content {
    background: #fff;
    border-radius: 6px;
    padding: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .tree-container {
    .tree-node {
      flex: 1;
      font-size: 14px;
      padding-right: 8px;

      .node-content {
        display: flex;
        align-items: center;
        width: 100%;
      }

      .node-info {
        display: flex;
        align-items: center;
        flex: 1;
      }

      .node-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        margin-right: 8px;
        font-size: 12px;
        flex-shrink: 0;

        &.team-icon {
          background: rgba(64, 158, 255, 0.1);
          border: 1px solid #409EFF;
          color: #409EFF;
        }

        &.group-icon {
          background: rgba(103, 194, 58, 0.1);
          border: 1px solid #67C23A;
          color: #67C23A;
        }

        &.member-icon {
          background: rgba(144, 147, 153, 0.1);
          border: 1px solid #909399;
          color: #909399;
        }
      }

      .node-content-wrapper {
        flex: 1;
        min-width: 0;

        .node-main {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
      }

      .node-label {
        font-weight: 600;
        color: #303133;
        font-size: 15px;

        &.clickable {
          cursor: pointer;
          transition: color 0.2s;

          &:hover {
            color: #409EFF;
          }
        }
      }

      .node-type-tag {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 500;
        white-space: nowrap;

        &.type-normal {
          background: #f6f8fa;
          color: #586069;
          border: 1px solid #d1d5da;
        }

        &.type-purple {
          background: #f3f0ff;
          color: #6f42c1;
          border: 1px solid #b794f6;
        }

        &.type-golden {
          background: #fef7e0;
          color: #d69e2e;
          border: 1px solid #f6d55c;
        }
      }

      .children-count-tag {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 500;
        white-space: nowrap;
        background: #e8f4fd;
        color: #1890ff;
        border: 1px solid #91d5ff;
      }

      .role-info {
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 10px;
        white-space: nowrap;

        &.team-leader-info {
          background: rgba(245, 101, 101, 0.1);
          color: #F56565;
          border: 1px solid rgba(245, 101, 101, 0.3);
        }

        &.leader-info {
          background: rgba(64, 158, 255, 0.1);
          color: #409EFF;
          border: 1px solid rgba(64, 158, 255, 0.3);
        }

        &.merchandiser-info {
          background: rgba(103, 194, 58, 0.1);
          color: #67C23A;
          border: 1px solid rgba(103, 194, 58, 0.3);
        }
      }

      .node-actions {
        opacity: 0;
        transition: opacity 0.3s;
        margin-left: 12px;

        .el-button--text {
          padding: 3px 6px;
          margin-left: 4px;
          font-size: 12px;
        }
      }

      &:hover .node-actions {
        opacity: 1;
      }
    }
  }
}

// 自定义树组件样式
::v-deep .el-tree {
  .el-tree-node {
    &:focus > .el-tree-node__content {
      background-color: #f5f7fa;
    }
  }

  .el-tree-node__content {
    height: auto;
    min-height: 40px;
    padding: 8px 0;

    &:hover {
      background-color: #f5f7fa;
    }
  }

  .el-tree-node__expand-icon {
    padding: 6px;
    color: #C0C4CC;

    &.is-leaf {
      color: transparent;
      cursor: default;
    }
  }
}
</style>
